import axios from 'axios';
import apiClient from './client';
import { API_BASE_URL } from '../utils/constants';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  role: string;
  first_name: string;
  last_name: string;
  dob?: string | null;
}

export interface UpdateUserPayload {
  first_name?: string;
  last_name?: string;
  dob?: string | null;
  terms_accepted?: boolean;
  weight_kg?: number | null;
  height_cm?: number | null;
  measurement_unit?: string | null;
}

export interface UploadProfilePictureResponse {
  id: string;
  email: string | null;
  first_name: string;
  last_name: string;
  profile_picture_url: string | null;
  updated_at?: string;
}

// Helper to construct full URL for local profile pictures
export function getProfilePictureUrl(url?: string | null, cacheBuster?: string | number): string | undefined {
  if (!url) return undefined;

  let fullUrl: string;

  // If it's already a full URL (GCS), return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    fullUrl = url;
  } else if (url.startsWith('/profile-pictures/')) {
    // If it's a relative path (local storage), construct full URL
    // Remove /api/v1 from API_BASE_URL to get the base
    const baseUrl = API_BASE_URL.replace('/api/v1', '');
    fullUrl = `${baseUrl}${url}`;
  } else {
    fullUrl = url;
  }

  // Add cache-busting query param if provided (e.g., user.updated_at timestamp).
  // The stored image path is deterministic (same path on re-upload), so the
  // token must change whenever the picture does or iOS shows the cached image.
  // Use the trailing digits of the timestamp — robust to millisecond OR
  // microsecond precision (client ISO has 3, Postgres has 6) and to a numeric
  // Date.now() value; the old 6-digit-only regex silently failed on 3-digit
  // client timestamps.
  if (cacheBuster) {
    const digits = String(cacheBuster).replace(/\D/g, '');
    const buster = digits.slice(-12) || String(cacheBuster);
    const separator = fullUrl.includes('?') ? '&' : '?';
    fullUrl = `${fullUrl}${separator}t=${buster}`;
  }

  return fullUrl;
}

export interface AuthSyncPayload {
  role?: string;
  first_name?: string;
  last_name?: string;
  dob?: string;
  terms_accepted?: boolean;
  weight_kg?: number | null;
  height_cm?: number | null;
  measurement_unit?: 'metric' | 'imperial';
  invite_token?: string;
}

export const usersApi = {
  login: (data: LoginPayload) =>
    apiClient.post('/users/login', data).then((r) => r.data),

  register: (data: RegisterPayload) =>
    apiClient.post('/users/register', data).then((r) => r.data),

  authSync: (neonJwt: string, data?: AuthSyncPayload) =>
    axios
      .post(`${API_BASE_URL}/users/auth/sync`, data || {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${neonJwt}`,
        },
      })
      .then((r) => r.data),

  getMe: () =>
    apiClient.get('/users/me').then((r) => r.data),

  updateMe: (data: UpdateUserPayload) =>
    apiClient.put('/users/me', data).then((r) => r.data),

  uploadProfilePicture: (formData: FormData) =>
    apiClient.post('/users/me/profile-picture', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((r) => r.data),

  deleteProfilePicture: () =>
    apiClient.delete('/users/me/profile-picture').then((r) => r.data),

  requestAccountDeletion: () =>
    apiClient.post('/users/me/request-deletion').then((r) => r.data),

  deactivateAccount: () =>
    apiClient.post('/users/me/deactivate').then((r) => r.data),

  };
